import { Button } from '@/components/ui';
import { RED_COLOR } from '@/constants/common';
import { trans } from '@/i18n';
import { Flex } from '@chakra-ui/react';
import { ReactNode } from 'react';

export interface ActionButton {
  key: string;
  text: string;
  onClick?: () => void;
  loading?: boolean;
  disabled?: boolean;
  variant?: 'solid' | 'outline' | 'ghost';
  bg?: string;
  color?: string;
  width?: string | number;
  height?: string | number;
  fontWeight?: string;
  border?: string;
  opacity?: number;
}

interface BaseFormActionsProps {
  actions: ActionButton[];
  justifyContent?: 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around';
  gap?: string | number;
  marginTop?: string | number;
  children?: ReactNode;
}

function BaseFormActions({
  actions,
  justifyContent = 'flex-end',
  gap = '20px',
  marginTop = '16px',
  children,
}: BaseFormActionsProps) {
  return (
    <Flex justifyContent={justifyContent} gap={gap} mt={marginTop}>
      {children}
      {actions.map((action) => (
        <Button
          key={action.key}
          onClick={action.onClick}
          loading={action.loading}
          disabled={action.disabled}
          variant={action.variant || 'outline'}
          width={action.width || '100px'}
          height={action.height || '35px'}
          bg={action.bg}
          color={action.color}
          fontWeight={action.fontWeight || 'bold'}
          border={action.border}
          opacity={action.opacity}
        >
          {action.text}
        </Button>
      ))}
    </Flex>
  );
}

export const createActions = {
  createEdit: (
    onSubmit: () => void,
    onCancel: () => void,
    loading = false,
    submitText = trans('plan.btnCreate')
  ): ActionButton[] => [
    {
      key: 'cancel',
      text: trans('shared.cancelText'),
      onClick: onCancel,
      loading: false,
      disabled: loading,
      variant: 'outline',
      color: '#1A202C',
      border: 'none',
      bg: '#EDF2F7',
    },
    {
      key: 'submit',
      text: submitText,
      onClick: onSubmit,
      loading,
      disabled: loading,
      variant: 'outline',
      bg: 'linear-gradient(81.62deg, #313860 2.25%, #151928 79.87%)',
      color: 'white',
    },
  ],

  detail: (
    onDelete?: () => void,
    onUpdate?: () => void,
    loadingDelete = false,
    loadingUpdate = false,
    hasUnsavedChanges = false
  ): ActionButton[] => [
    {
      key: 'delete',
      text: trans('shared.delete'),
      onClick: onDelete,
      loading: loadingDelete,
      disabled: loadingDelete || loadingUpdate,
      bg: RED_COLOR,
      color: 'white',
    },
    {
      key: 'update',
      text: trans('shared.update'),
      onClick: onUpdate,
      loading: loadingUpdate,
      disabled: loadingDelete || loadingUpdate || !hasUnsavedChanges,
      variant: 'outline',
      bg: hasUnsavedChanges
        ? 'linear-gradient(81.62deg, #313860 2.25%, #151928 79.87%)'
        : 'transparent',
      color: hasUnsavedChanges ? 'white' : '#6A6A6A',
      border: hasUnsavedChanges ? 'none' : '1px solid #6A6A6A',
      opacity: 1,
    },
  ],
};

export { BaseFormActions };
export default BaseFormActions;
